/*
 * NOSH FINAL CLOSURE, 7 August 2026 — global PDP lightbox controller.
 *
 * Extends Dawn's ModalDialog, so everything that was already measured
 * working on the beta is inherited untouched: Escape closes, focus is
 * trapped while open, focus returns to the opener, and background scroll
 * locks and restores.
 *
 * What this adds, and why:
 *   - Fit-first. The stage CSS constrains the media to the viewport; this
 *     controller resets scale/pan to Fit on every open and on every media
 *     change, so a customer is never dropped into a crop.
 *   - Dawn's `container.scrollLeft = (activeMedia.width - container.clientWidth) / 2`
 *     is REMOVED. That single line is what produced the measured centre-crop
 *     (scrollLeft 247 of a 1100px image in a 606px stage).
 *   - Zoom in / out / reset, pointer pan above Fit only, pinch, trackpad
 *     pinch, double-tap toggle, and keyboard +/-/0 and arrow keys.
 *   - One media at a time with prev/next and an "n of N" counter.
 *
 * No external library. No extra network request: zooming re-uses the srcset
 * candidate the browser already selected for the stage.
 */
if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.MIN_SCALE = 1;
        this.MAX_SCALE = 4;
        this.STEP = 0.5;

        this.scale = 1;
        this.tx = 0;
        this.ty = 0;

        this.pointers = new Map();
        this.pinchStartDist = 0;
        this.pinchStartScale = 1;
        this.dragging = false;
        this.dragStart = null;
        this.lastTapAt = 0;

        this.content = this.querySelector('[role="document"]');
        this.controls = {
          zoomIn: this.querySelector('[data-mzoom-in]'),
          zoomOut: this.querySelector('[data-mzoom-out]'),
          reset: this.querySelector('[data-mzoom-reset]'),
          level: this.querySelector('[data-mzoom-level]'),
          prev: this.querySelector('[data-mzoom-prev]'),
          next: this.querySelector('[data-mzoom-next]'),
          counter: this.querySelector('[data-mzoom-counter]'),
        };

        this.bindControls();
        this.bindGestures();
      }

      /* ---------------- media list ---------------- */

      get mediaItems() {
        if (!this.content) return [];
        return Array.from(this.content.querySelectorAll(':scope > [data-media-id]'));
      }

      get activeMedia() {
        return this.content ? this.content.querySelector(':scope > .active') : null;
      }

      isZoomable(el) {
        return !!el && el.tagName === 'IMG';
      }

      /* ---------------- lifecycle ---------------- */

      hide() {
        this.resetZoom(false);
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        const wanted = this.openedBy && this.openedBy.getAttribute('data-media-id');
        const items = this.mediaItems;
        if (!items.length) return;

        const target = this.content.querySelector(`:scope > [data-media-id="${wanted}"]`) || items[0];

        items.forEach((el) => el.classList.toggle('active', el === target));

        this.resetZoom(false);
        this.syncControls();

        const tpl = target.querySelector('template');
        const content = tpl ? tpl.content : null;
        if (target.nodeName === 'DEFERRED-MEDIA' && content && content.querySelector('.js-youtube')) {
          target.loadContent();
        }
      }

      step(delta) {
        const items = this.mediaItems;
        if (items.length < 2) return;
        const current = items.indexOf(this.activeMedia);
        const next = (current + delta + items.length) % items.length;

        if (window.pauseAllMedia) window.pauseAllMedia();
        items.forEach((el, i) => el.classList.toggle('active', i === next));

        this.resetZoom(false);
        this.syncControls();
      }

      /* ---------------- zoom maths ---------------- */

      clampPan() {
        const el = this.activeMedia;
        if (!el || !this.isZoomable(el)) {
          this.tx = 0;
          this.ty = 0;
          return;
        }
        // The element is laid out at Fit size; scaling happens about its
        // centre, so the overflow available to pan is half the difference
        // between the scaled box and the stage.
        const stage = this.content.getBoundingClientRect();
        const w = el.offsetWidth * this.scale;
        const h = el.offsetHeight * this.scale;
        const maxX = Math.max(0, (w - stage.width) / 2);
        const maxY = Math.max(0, (h - stage.height) / 2);
        this.tx = Math.min(maxX, Math.max(-maxX, this.tx));
        this.ty = Math.min(maxY, Math.max(-maxY, this.ty));
      }

      applyTransform() {
        const el = this.activeMedia;
        if (!el) return;
        if (!this.isZoomable(el) || this.scale === 1) {
          el.style.transform = '';
        } else {
          // translate() before scale(): the translation stays in untransformed
          // pixels, which is what clampPan() measures against.
          el.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
        }
        this.content.classList.toggle('is-zoomed', this.scale > 1);
      }

      setScale(next, originX, originY) {
        const el = this.activeMedia;
        if (!this.isZoomable(el)) return;

        const clamped = Math.min(this.MAX_SCALE, Math.max(this.MIN_SCALE, Math.round(next * 100) / 100));
        if (clamped === this.scale) return;

        if (typeof originX === 'number' && typeof originY === 'number') {
          // Keep the point under the cursor / pinch centre visually stable.
          const rect = el.getBoundingClientRect();
          const cx = rect.left + rect.width / 2;
          const cy = rect.top + rect.height / 2;
          const ratio = clamped / this.scale;
          this.tx = originX - ratio * (originX - (this.tx + cx)) - cx;
          this.ty = originY - ratio * (originY - (this.ty + cy)) - cy;
        }

        this.scale = clamped;
        if (this.scale === 1) {
          this.tx = 0;
          this.ty = 0;
        }
        this.clampPan();
        this.applyTransform();
        this.syncControls();
      }

      resetZoom(sync) {
        const el = this.activeMedia;
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        if (el) el.style.transform = '';
        if (this.content) {
          this.content.classList.remove('is-zoomed', 'is-dragging');
        }
        if (sync !== false) this.syncControls();
      }

      syncControls() {
        const zoomable = this.isZoomable(this.activeMedia);
        const c = this.controls;

        if (c.level) c.level.textContent = this.scale === 1 ? 'Fit' : `${Math.round(this.scale * 100)}%`;
        if (c.zoomIn) c.zoomIn.disabled = !zoomable || this.scale >= this.MAX_SCALE;
        if (c.zoomOut) c.zoomOut.disabled = !zoomable || this.scale <= this.MIN_SCALE;
        if (c.reset) c.reset.disabled = this.scale === 1;

        if (c.counter) {
          const items = this.mediaItems;
          const i = items.indexOf(this.activeMedia);
          c.counter.textContent = i > -1 ? `${i + 1} of ${items.length}` : '';
        }
      }

      /* ---------------- bindings ---------------- */

      bindControls() {
        const c = this.controls;
        if (c.zoomIn) c.zoomIn.addEventListener('click', () => this.setScale(this.scale + this.STEP));
        if (c.zoomOut) c.zoomOut.addEventListener('click', () => this.setScale(this.scale - this.STEP));
        if (c.reset) c.reset.addEventListener('click', () => this.resetZoom());
        if (c.prev) c.prev.addEventListener('click', () => this.step(-1));
        if (c.next) c.next.addEventListener('click', () => this.step(1));

        this.addEventListener('keydown', (e) => {
          if (e.key === '+' || e.key === '=') {
            e.preventDefault();
            this.setScale(this.scale + this.STEP);
          } else if (e.key === '-' || e.key === '_') {
            e.preventDefault();
            this.setScale(this.scale - this.STEP);
          } else if (e.key === '0') {
            e.preventDefault();
            this.resetZoom();
          } else if (e.key === 'ArrowRight' && this.scale === 1) {
            e.preventDefault();
            this.step(1);
          } else if (e.key === 'ArrowLeft' && this.scale === 1) {
            e.preventDefault();
            this.step(-1);
          }
        });
      }

      bindGestures() {
        if (!this.content) return;
        const stage = this.content;

        // Trackpad pinch and ctrl+wheel. Plain wheel is left alone so the
        // page behind never scrolls unexpectedly.
        stage.addEventListener(
          'wheel',
          (e) => {
            if (!e.ctrlKey || !this.isZoomable(this.activeMedia)) return;
            e.preventDefault();
            this.setScale(this.scale - e.deltaY * 0.01, e.clientX, e.clientY);
          },
          { passive: false }
        );

        stage.addEventListener('pointerdown', (e) => {
          if (!this.isZoomable(this.activeMedia)) return;
          this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

          if (this.pointers.size === 2) {
            const [a, b] = Array.from(this.pointers.values());
            this.pinchStartDist = Math.hypot(a.x - b.x, a.y - b.y);
            this.pinchStartScale = this.scale;
            this.dragging = false;
            stage.classList.remove('is-dragging');
            return;
          }

          // Double-tap / double-click toggles Fit <-> 2x.
          const now = Date.now();
          if (now - this.lastTapAt < 300) {
            this.lastTapAt = 0;
            if (this.scale > 1) this.resetZoom();
            else this.setScale(2, e.clientX, e.clientY);
            return;
          }
          this.lastTapAt = now;

          // Pan is only ever available above Fit, so at Fit there is no
          // accidental image drag.
          if (this.scale > 1) {
            this.dragging = true;
            this.dragStart = { x: e.clientX - this.tx, y: e.clientY - this.ty };
            stage.classList.add('is-dragging');
            if (stage.setPointerCapture) {
              try {
                stage.setPointerCapture(e.pointerId);
              } catch (err) {
                /* non-fatal */
              }
            }
          }
        });

        stage.addEventListener('pointermove', (e) => {
          if (!this.pointers.has(e.pointerId)) return;
          this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

          if (this.pointers.size === 2 && this.pinchStartDist > 0) {
            e.preventDefault();
            const [a, b] = Array.from(this.pointers.values());
            const dist = Math.hypot(a.x - b.x, a.y - b.y);
            this.setScale(
              this.pinchStartScale * (dist / this.pinchStartDist),
              (a.x + b.x) / 2,
              (a.y + b.y) / 2
            );
            return;
          }

          if (this.dragging && this.dragStart) {
            e.preventDefault();
            this.tx = e.clientX - this.dragStart.x;
            this.ty = e.clientY - this.dragStart.y;
            this.clampPan();
            this.applyTransform();
          }
        });

        const endPointer = (e) => {
          this.pointers.delete(e.pointerId);
          if (this.pointers.size < 2) this.pinchStartDist = 0;
          if (this.pointers.size === 0) {
            this.dragging = false;
            this.dragStart = null;
            stage.classList.remove('is-dragging');
          }
        };
        stage.addEventListener('pointerup', endPointer);
        stage.addEventListener('pointercancel', endPointer);
        stage.addEventListener('pointerleave', endPointer);

        // A zoomed image must never be draggable as a ghost image.
        stage.addEventListener('dragstart', (e) => {
          if (this.scale > 1) e.preventDefault();
        });

        window.addEventListener('resize', () => {
          if (this.scale > 1) {
            this.clampPan();
            this.applyTransform();
          }
        });
      }
    }
  );
}
